import { ChangeDetectorRef, Component, Optional, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { NbDialogRef, NbDialogService, NbCardModule, NbButtonModule, NbSpinnerModule } from '@nebular/theme';
import { RightPanelDialogService } from '../../../@core/utils/right-panel-dialog.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { CommonModule, DatePipe } from '@angular/common';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { KPI } from '../../../@core/api';
import { AssetsService } from '../../../@core/utils/assets.service';
import { ComplianceService } from '../../../@core/utils/compliance.service';
import { DocumentsService } from '../../../@core/utils/documents.service';
import { StateLevelsService } from '../../../@core/utils/state-levels.service';
import { I18nService } from '../../../@core/utils/i18n.service';
import { QuantityUnitsService } from '../../../@core/utils/quantity-units.service';
import { YearsService } from '../../../@core/utils/years.service';
import { of, combineLatest, forkJoin } from 'rxjs';
import { map, catchError, take, first, switchMap, tap } from 'rxjs/operators';
import { AttachEquipmentDialogComponent } from '../attach-equipment-dialog/attach-equipment-dialog.component';
import { InvestmentsService } from '../../../@core/utils';
import { SaveToastService } from '../../../@core/utils/save-toast.service';
import { BasicDialogComponent } from '../basic-dialog/basic-dialog.component';
import { FutureEquipmentFormComponent } from '../future-equipment-form/future-equipment-form.component';

@Component({
  selector: 'app-linked-equipment-dialog',
  templateUrl: './linked-equipment-dialog.component.html',
  styleUrls: ['./linked-equipment-dialog.component.scss'],
  standalone: true,
  imports: [
    NbCardModule,
    NbButtonModule,
    NbSpinnerModule,
    TranslateModule,
    CommonModule,
    FontAwesomeModule,
    FutureEquipmentFormComponent,
  ],
  providers: [DatePipe]
})
export class LinkedEquipmentDialogComponent implements OnInit, OnDestroy {
  @ViewChild('futureForm') public futureForm!: FutureEquipmentFormComponent;

  public equipmentList: KPI.MappedAsset[] = [];
  public loading: boolean = false;
  public linkedEquipmentIds: number[] = [];
  public asset: KPI.MappedAsset | null = null;
  public reportId: number | null = null;
  public documentId: number | null = null;
  public siteId: number | null = null;
  public investmentId: number | null = null;
  public readOnly: boolean = false;
  public title: string = this.translate.instant('asset_form_drawer.compliance.control_report.equipements_lies');
  public multipleChoice: boolean = true; // If true, the user can select multiple assets
  public endOfLifeYear: number | null = null;
  public nextAsset: KPI.MappedAsset | null = null;
  public currentAsset: KPI.MappedAsset | null = null;
  public isInvestmentClosed: boolean = false;
  public installationYear: number | null = null;
  private quantityUnitsCache: Map<number, KPI.QuantityUnit> = new Map();
  public investmentType: KPI.InvestmentType | null = null;
  private superCategoryId: number | null = null;
  private categoryId: number | null = null;
  private subCategoryId: number | null = null;

  constructor(
    @Optional() public ref: NbDialogRef<LinkedEquipmentDialogComponent>,
    private assetsService: AssetsService,
    private complianceService: ComplianceService,
    private documentsService: DocumentsService,
    private dialogService: NbDialogService,
    private stateLevelsService: StateLevelsService,
    private translate: TranslateService,
    private datePipe: DatePipe,
    private i18n: I18nService,
    private quantityUnitsService: QuantityUnitsService,
    private yearsService: YearsService,
    private investmentsService: InvestmentsService,
    private saveToast: SaveToastService,
    private rightPanelDialogService: RightPanelDialogService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    const context = (this.ref as any)?.context;
    if (context) {
      this.linkedEquipmentIds = context.linkedEquipmentIds || [];
      this.asset = context.asset || null;
      this.reportId = context.reportId || null;
      this.documentId = context.documentId || null;
      this.siteId = context.siteId || null;
      this.readOnly = context.readOnly ?? (this.reportId === null && this.documentId === null);
      this.investmentId = context.investmentId || null;
      this.multipleChoice = context.multipleChoice || true;
      if (context.title) this.title = context.title;
      if (context.investmentType) this.investmentType = context.investmentType;
    }
    this.loadQuantityUnits();
    this.loadEquipment();
    this.initializeBackdropClick();
    this.investmentsService.getInvestment(this.investmentId!, true).pipe(
      first(),
      tap(investment => {
        if (investment) {
          this.superCategoryId = investment.super_category ?? null;
          this.categoryId = investment.category ?? null;
          this.subCategoryId = investment.sub_category ?? null;
          this.isInvestmentClosed = investment.status_object?.is_closed || false;
          this.installationYear = investment.final_schedule_to || null;
        }
      }),
      switchMap(investment => {
        if (investment?.next_asset) {
          return this.assetsService.getAsset(investment.next_asset);
        }
        return of(null);
      }),
    ).subscribe(nextAsset => {
      this.nextAsset = nextAsset as KPI.MappedAsset | null;
      this.cdr.detectChanges();
    });
  }

  private loadQuantityUnits(): void {
    this.quantityUnitsService.quantityUnits$.pipe(first()).subscribe(quantityUnits => {
      quantityUnits.forEach(unit => {
        this.quantityUnitsCache.set(unit.id, unit);
      });
    });
  }

  private initializeBackdropClick(): void {
    if (this.ref && (this.ref as any).onBackdropClick) {
      (this.ref as any).onBackdropClick.pipe(take(1)).subscribe(() => {
        this.cancel();
      });
    }
  }

  public loadEquipment(): void {
    if (!this.linkedEquipmentIds || this.linkedEquipmentIds.length === 0) {
      this.equipmentList = [];
      return;
    }

    combineLatest([
      of(this.siteId),
      this.yearsService.startYear$.pipe(first())
    ]).pipe(
      switchMap(([perimeterId, year]) => {
        if (!perimeterId || !year) {
          this.loading = true;
          return this.assetsService.getAssets(this.linkedEquipmentIds).pipe(
            catchError(() => of([]))
          );
        }

        if (!this.assetsService.hasCachedEquipment(perimeterId, year)) {
          this.loading = true;
        }

        return this.assetsService.getEquipmentFromSynthesis(perimeterId, year, 0).pipe(
          map((equipment) => {
            const linkedIdsSet = new Set(this.linkedEquipmentIds);
            return (equipment || []).filter(eq => linkedIdsSet.has(eq.id));
          }),
          catchError(() => {
            return this.assetsService.getAssets(this.linkedEquipmentIds).pipe(
              catchError(() => of([]))
            );
          })
        );
      }),
      tap(equipment => {
        if (equipment?.length === 1) this.getAssetEndOfLifeYear(equipment[0].id);
      }),
      first(),
    ).subscribe({
      next: (equipment) => {
        this.equipmentList = equipment || [];
        this.currentAsset = this.equipmentList[0] || null;
        this.loading = false;
      },
      error: () => {
        this.equipmentList = [];
        this.loading = false;
      }
    });
  }

  public get isTempMode(): boolean {
    return this.documentId === -1;
  }

  public get showFutureEquipment(): boolean {
    return !!(this.nextAsset) || !!(this.investmentType?.creation_of_equipment);
  }

  public get isCreationType(): boolean {
    return !!(this.investmentType?.creation_of_equipment);
  }

  public onNextAssetSaved(_updatedAsset: KPI.Asset): void {
    this.investmentsService.reloadInvestment$.next(this.investmentId!);
    this.ref?.close();
  }

  public openAttachEquipmentDialog(): void {
    if (!this.reportId && !this.documentId && !this.investmentId) {
      return;
    }

    let siteId: number | null = this.siteId || null;
    if (!siteId && this.asset?.perimeters && this.asset.perimeters.length > 0) {
      const site = this.asset.perimeters.find(p => !p.level_parent || !p.is_monosite)
        || this.asset.perimeters[0];
      siteId = site.id;
    }

    this.rightPanelDialogService.open(AttachEquipmentDialogComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      hasScroll: false,
      backdropClass: 'control-report-backdrop',
      dialogClass: 'attach-equipment-dialog-overlay',
      context: {
        reportId: this.reportId,
        documentId: this.documentId,
        asset: this.asset,
        siteId: siteId,
        linkedEquipmentIds: this.linkedEquipmentIds,
        multipleChoice: this.multipleChoice,
        investmentId: this.investmentId,
        superCategoryId: this.superCategoryId,
        categoryId: this.categoryId,
        subCategoryId: this.subCategoryId
      } as any
    }).onClose.pipe(
      first(),
      switchMap((result: number[] | boolean) => {
        if (!result) {
          return of(false);
        }
        // Temp mode (new document not yet saved): skip API, update local state only
        if (Array.isArray(result) && this.isTempMode) {
          this.linkedEquipmentIds = result;
          this.setSaved();
          return of(true);
        }
        // Document mode: AttachEquipmentDialog closes with merged number[]
        if (Array.isArray(result) && this.documentId) {
          this.setSaving();
          return this.documentsService.updateDocumentLinkedEquipment(this.documentId, result).pipe(
            map(updated => {
              this.linkedEquipmentIds = updated.linked_equipment || [];
              this.setSaved();
              return true;
            }),
            catchError(() => of(true))
          );
        }
        // Report mode: re-fetch the updated report to get the new linked_equipment list
        if (this.reportId) {
          return this.complianceService.getControlOfficeReport(this.reportId).pipe(
            map(report => {
              this.linkedEquipmentIds = report.linked_equipment || [];
              return true;
            }),
            catchError(() => of(true))
          );
        }
        if (this.investmentId && Array.isArray(result)) {
          this.attachInvestmentAndAsset(result[0])
        }
        return of(false);
      })
    ).subscribe((shouldReload) => {
      if (shouldReload) {
        this.loadEquipment();
      }
    });
  }

  public confirmTempSelection(): void {
    this.ref.close(this.linkedEquipmentIds);
  }

  public cancel(): void {
    this.ref.close(this.linkedEquipmentIds);
  }

  public getEquipmentThumbnail(equipment: KPI.MappedAsset): string | null {
    const images = equipment.pictures;
    if (!images || images.length === 0) {
      return null;
    }
    const image = images.find(i => i.thumbnail) || images[0];
    if (!image?.thumbnail) {
      return null;
    }
    let thumbnail = image.thumbnail.trim();
    thumbnail = thumbnail.replace(/["']/g, '');
    return thumbnail || null;
  }

  public getPerimeterHierarchy(equipment: KPI.MappedAsset): string {
    if (equipment.perimeters && equipment.perimeters.length > 0) {
      const site = equipment.perimeters.find(p => !p.is_monosite);
      const equipmentPerimeter = equipment.perimeters.find(p => p.is_monosite);
      if (equipmentPerimeter) {
        if (equipmentPerimeter.level_parent && site) {
          const parentPerimeter = site.sub_perimeters?.find(p => p.id === equipmentPerimeter.level_parent);
          if (parentPerimeter) {
            return parentPerimeter.name + " > " + equipmentPerimeter.name;
          } else {
            return equipmentPerimeter.name;
          }
        } else {
          return equipmentPerimeter.name;
        }
      }
    }

    const buildingObj = (equipment as any).building_object;
    if (buildingObj && buildingObj.monosite_perimeter) {
      const monositePerimeter = buildingObj.monosite_perimeter;
      if (typeof monositePerimeter === 'object' && monositePerimeter.name) {
        return monositePerimeter.name;
      }
    }

    if (buildingObj) {
      if (buildingObj.name) {
        return buildingObj.name;
      }
      if (buildingObj.site_name) {
        return buildingObj.site_name;
      }
    }

    if (equipment.site_name) {
      return equipment.site_name;
    }
    return '-';
  }

  public getLastEvaluation(equipment: KPI.MappedAsset): string {
    let notation: number | null = null;
    let date: Date | null = null;

    if (equipment.last_notation) {
      notation = equipment.last_notation.note;
      date = equipment.last_notation.audit_year
        ? new Date(equipment.last_notation.audit_year, 0, 1)
        : null;

      if (notation === null || notation === undefined) {
        return this.formatTechnicalStateWithDate(
          this.translate.instant('core.utils.state-levels.no_notation'),
          date
        );
      }

      const label = this.stateLevelsService.getTechnicalStateLabelFromNote(notation);
      return this.formatTechnicalStateWithDate(label, date);
    }

    if (equipment.asset_notations && equipment.asset_notations.length > 0) {
      const technicalStateNotations = equipment.asset_notations
        .filter((n: any) => {
          const questionObj = (n as any).audit_question_object;
          return questionObj && questionObj.mnemonic === 'technical_state';
        })
        .sort((a: any, b: any) => (a.audit_year || 0) - (b.audit_year || 0));

      if (technicalStateNotations.length > 0) {
        const lastNotation = technicalStateNotations[technicalStateNotations.length - 1];
        notation = lastNotation.note;
        date = lastNotation.audit_year
          ? new Date(lastNotation.audit_year, 0, 1)
          : null;

        if (notation !== null && notation !== undefined) {
          const label = this.stateLevelsService.getTechnicalStateLabelFromNote(notation);
          return this.formatTechnicalStateWithDate(label, date);
    }
      }
    }

    if ((equipment as any).last_technical_evaluation !== undefined) {
      const levelNum = (equipment as any).last_technical_evaluation;
      notation = this.stateLevelsService.getTechnicalStateNotationFromLevel(levelNum);
      if (notation !== null) {
        date = (equipment as any).last_evaluation_date || (equipment as any).technical_state_changed_on;
        const label = this.stateLevelsService.getTechnicalStateLabelFromNote(notation);
        return this.formatTechnicalStateWithDate(label, date ? new Date(date) : null);
      }
    }

    const level = (equipment as any).level;
    if (level && typeof level === 'string' && level.trim() !== '') {
      date = (equipment as any).technical_state_changed_on
        ? new Date((equipment as any).technical_state_changed_on)
        : null;
      const label = this.getLevelLabelFromString(level);
      return this.formatTechnicalStateWithDate(label, date);
      }

    date = (equipment as any).last_evaluation_date || (equipment as any).technical_state_changed_on;
    return this.formatTechnicalStateWithDate(
      this.translate.instant('core.utils.state-levels.no_notation'),
      date ? new Date(date) : null
    );
  }

  private formatTechnicalStateWithDate(label: string, date: Date | null): string {
    if (date === null) {
      return label;
    }

    const formattedDate = this.datePipe.transform(date, 'd MMMM yyyy', undefined, this.translate.currentLang) || '';
    return `${this.translate.instant('Last evaluation on')} ${formattedDate}: ${label}`;
  }

  public getLastEvaluationDate(equipment: KPI.MappedAsset): string {
    const level = (equipment as any).level;
    let date: Date | null = null;
    if (equipment.last_notation) {
      date = equipment.last_notation.audit_year ? new Date(equipment.last_notation.audit_year, 0, 1) : null;
    } else if (equipment.asset_notations && equipment.asset_notations.length > 0) {
      const technicalStateNotations = equipment.asset_notations
        .filter((n: any) => {
          const questionObj = (n as any).audit_question_object;
          return questionObj && questionObj.mnemonic === 'technical_state';
        })
        .sort((a: any, b: any) => (a.audit_year || 0) - (b.audit_year || 0));
      if (technicalStateNotations.length > 0) {
        const lastNotation = technicalStateNotations[technicalStateNotations.length - 1];
        date = lastNotation.audit_year ? new Date(lastNotation.audit_year, 0, 1) : null;
      }
    } else if ((equipment as any).last_technical_evaluation !== undefined) {
      date = (equipment as any).last_evaluation_date || (equipment as any).technical_state_changed_on;
      date = date ? new Date(date) : null;
    } else {
      if (level && typeof level === 'string' && level.trim() !== '') {
        date = (equipment as any).technical_state_changed_on ? new Date((equipment as any).technical_state_changed_on) : null;
      } else {
        date = (equipment as any).last_evaluation_date || (equipment as any).technical_state_changed_on;
        date = date ? new Date(date) : null;
      }
    }
    if (date === null) {
      return '';
    }
    return this.formatTechnicalStateWithDate('', date)
  }

  public getLastEvaluationStatus(equipment: KPI.MappedAsset): string {
    let notation: number | null = null;

    if (equipment.last_notation) {
      notation = equipment.last_notation.note;
      if (notation === null || notation === undefined) {
        return this.translate.instant('core.utils.state-levels.no_notation');
      }
      return this.stateLevelsService.getTechnicalStateLabelFromNote(notation);
    }

    if (equipment.asset_notations && equipment.asset_notations.length > 0) {
      const technicalStateNotations = equipment.asset_notations
        .filter((n: any) => {
          const questionObj = (n as any).audit_question_object;
          return questionObj && questionObj.mnemonic === 'technical_state';
        })
        .sort((a: any, b: any) => (a.audit_year || 0) - (b.audit_year || 0));

      if (technicalStateNotations.length > 0) {
        const lastNotation = technicalStateNotations[technicalStateNotations.length - 1];
        notation = lastNotation.note;
        if (notation !== null && notation !== undefined) {
          return this.stateLevelsService.getTechnicalStateLabelFromNote(notation);
        }
      }
    }

    if ((equipment as any).last_technical_evaluation !== undefined) {
      const levelNum = (equipment as any).last_technical_evaluation;
      notation = this.stateLevelsService.getTechnicalStateNotationFromLevel(levelNum);
      if (notation !== null) {
        return this.stateLevelsService.getTechnicalStateLabelFromNote(notation);
      }
    }

    const level = (equipment as any).level;
    if (level && typeof level === 'string' && level.trim() !== '') {
      return this.getLevelLabelFromString(level);
    }

    return this.translate.instant('core.utils.state-levels.no_notation');
  }

  public isLastEvaluationCritical(equipment: KPI.MappedAsset): boolean {
    const level = (equipment as any).level;
    if (level) {
      const levelLower = level.toLowerCase();
      return levelLower === 'dead' || levelLower === 'critical';
    }

    if (equipment.last_notation) {
      const notation = equipment.last_notation.note;
      if (notation === null || notation === undefined) {
        return true;
      }
      return notation <= 25;
    }

    if ((equipment as any).last_technical_evaluation !== undefined) {
      const levelNum = (equipment as any).last_technical_evaluation;
      const notation = this.stateLevelsService.getTechnicalStateNotationFromLevel(levelNum);
      if (notation !== null) {
        return notation <= 25;
      }
      return false;
    }

    const statusText = this.getLastEvaluationStatus(equipment).toLowerCase();
    const criticalStatuses = [
      this.translate.instant('core.utils.state-levels.critical').toLowerCase(),
      'critique'
    ];

    if (criticalStatuses.some(critical => statusText.includes(critical))) {
      return true;
    }

    const evaluationText = this.getLastEvaluation(equipment);
    return evaluationText.includes(this.translate.instant('core.utils.state-levels.no_notation'));
  }

  private getLevelLabelFromString(level: string): string {
    const levelLower = level.toLowerCase();
    const levelMap: { [key: string]: string } = {
      'dead': 'core.utils.state-levels.critical',
      'critical': 'core.utils.state-levels.critical',
      'bad': 'core.utils.state-levels.deteriorated',
      'ageing': 'core.utils.state-levels.deteriorated',
      'deteriorated': 'core.utils.state-levels.deteriorated',
      'acceptable': 'core.utils.state-levels.acceptable',
      'good': 'core.utils.state-levels.good',
      'new': 'core.utils.state-levels.good'
    };

    const transKey = levelMap[levelLower];
    if (transKey) {
      return this.translate.instant(transKey);
    }
    return this.translate.instant(level) !== level ? this.translate.instant(level) : level;
  }

  public getQuantity(equipment: KPI.MappedAsset): string | null {
    const quantity = equipment.quantity;
    let quantityUnit: KPI.QuantityUnit | null = null;
    if ((equipment as any).quantity_unit) {
      const unit = (equipment as any).quantity_unit;
      if (typeof unit === 'object' && unit.name) {
        quantityUnit = unit;
      } else if (typeof unit === 'number') {
        quantityUnit = this.quantityUnitsCache.get(unit) || null;
      }
    }
    if (!quantityUnit && equipment.asset_type_object && (equipment.asset_type_object as any).quantity_unit) {
      const unitId = (equipment.asset_type_object as any).quantity_unit;
      if (typeof unitId === 'number') {
        quantityUnit = this.quantityUnitsCache.get(unitId) || null;
      } else if (typeof unitId === 'object' && unitId.name) {
        quantityUnit = unitId as KPI.QuantityUnit;
      }
    }
    const unitName = quantityUnit && quantityUnit.name ? quantityUnit.name : 'u';
    if (quantityUnit?.setup_reference !== 'no_unit') {
      return `${this.translate.instant('Quantity')}: ${this.i18n.formatNumber(quantity)} ${unitName}`;
    } else {
      return null;
    }
  }

  public showQuantityUnit(asset: KPI.MappedAsset): boolean {
    const unitId = asset.asset_type_object.quantity_unit;
    if (!unitId) return false;
    return this.quantityUnitsCache.get(unitId)?.setup_reference !== 'no_unit';
  }

  public detachEquipment(equipment: KPI.MappedAsset, event?: Event): void {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (!equipment.id) {
      return;
    }

    if (this.investmentId) {
      this.detachInvestmentAndAsset(equipment.id);
    }

    const updatedLinkedEquipment = (this.linkedEquipmentIds || []).filter(id => id !== equipment.id);

    // Temp mode (new document not yet saved): skip API, update local state only
    if (this.isTempMode) {
      this.linkedEquipmentIds = updatedLinkedEquipment;
      this.loadEquipment();
      this.setSaved();
      return;
    }

    if (this.documentId) {
      this.setSaving();
      this.documentsService.updateDocumentLinkedEquipment(this.documentId, updatedLinkedEquipment).pipe(
        first(),
        catchError(() => of(null))
      ).subscribe(result => {
        if (result) {
          this.linkedEquipmentIds = result.linked_equipment || [];
          this.loadEquipment();
          this.setSaved();
        } else {
          this.saveToast.close();
        }
      });
      return;
    }

    if (!this.reportId) {
      return;
    }

    const compliance = (equipment as any)?.compliance;
    let equipmentComplianceId: number | null = null;
    if (compliance) {
      equipmentComplianceId = typeof compliance === 'object' && compliance.id ? compliance.id : compliance;
      equipmentComplianceId = equipmentComplianceId && equipmentComplianceId > 0 ? equipmentComplianceId : null;
    }

    const getComplianceId$ = equipmentComplianceId
      ? of(equipmentComplianceId)
      : this.assetsService.getAsset(equipment.id).pipe(
          first(),
          map(asset => {
            const compliance = (asset as any)?.compliance;
            const complianceId = compliance && (typeof compliance === 'object' && compliance.id ? compliance.id : compliance);
            return complianceId && complianceId > 0 ? complianceId : null;
          }),
          catchError(() => of(null))
        );

    getComplianceId$.pipe(
      switchMap(complianceId => {
        return this.complianceService.patchControlOfficeReport(this.reportId!, {
          linked_equipment: updatedLinkedEquipment
        }).pipe(
          first(),
          switchMap(updatedReport => {
            if (!updatedReport) {
              return of(null);
            }

            if (!complianceId) {
              return of(updatedReport);
            }

            return this.complianceService.getReportReservations(this.reportId!).pipe(
              first(),
              switchMap(reserves => {
                if (!reserves || reserves.length === 0) {
                  return of(updatedReport);
                }

                const reservesToUnlink = reserves.filter(reserve =>
                  reserve.compliance === complianceId
                );

                if (reservesToUnlink.length === 0) {
                  return of(updatedReport);
                }

                const reserveUpdateRequests = reservesToUnlink.map(reserve => {
                  return this.complianceService.updateReservation(reserve.id, {
                    compliance: null as any
                  } as KPI.ReservationUpdatePayload).pipe(
                    catchError(() => of(null))
                  );
                });

                return forkJoin(reserveUpdateRequests).pipe(
                  map(() => updatedReport),
                  catchError(() => of(updatedReport))
                );
              }),
              catchError(() => of(updatedReport))
            );
          }),
          catchError(() => of(null))
        );
      }),
      catchError(() => of(null))
    ).subscribe(updatedReport => {
      if (updatedReport) {
        this.linkedEquipmentIds = updatedReport.linked_equipment || [];
        this.loadEquipment();
        this.setSaved();
      }
    });
  }

  private setSaving(): void {
    this.saveToast.showSaving();
  }

  private setSaved(): void {
    this.saveToast.showSaved();
  }

  ngOnDestroy(): void {
  }

  private attachInvestmentAndAsset(assetId: number) {
    this.investmentsService.attachInvestmentAndAsset(this.investmentId!, assetId).pipe(first())
    .subscribe(() => {
      this.linkedEquipmentIds = [assetId];
      this.loadEquipment();
      this.investmentsService.reloadInvestment$.next(this.investmentId!);
    })
  }

  private detachInvestmentAndAsset(assetId: number) {
    if (this.investmentType?.replacement) {
      this.dialogService.open(BasicDialogComponent, {
        context: {
          title: this.translate.instant('Detach equipment'),
          content: this.translate.instant("You can't detach the equipment for an investment of type replacement") + '.',
          contentBis: this.translate.instant("If the linked equipment isn't the correct one, please delete the investment then recreate it choosing the correct linked equipment") + "."
        }
      });
      return;
    }
    this.dialogService.open(BasicDialogComponent, {
      context: {
        title: this.translate.instant('Detach equipment'),
        content: this.translate.instant('Do you want to continue?'),
        canCancel: true,
      }
    }).onClose.subscribe(res => {
      if (res) {
        this.investmentsService.detachInvestmentAndAsset(this.investmentId!, assetId).pipe(first())
        .subscribe(() => {
          this.linkedEquipmentIds = [];
          this.loadEquipment();
          this.setSaved();
          this.investmentsService.reloadInvestment$.next(this.investmentId!);
        })
      }
    })
  }

  public showLinkButton(): boolean {
    return (
      !this.readOnly &&
      (this.multipleChoice || this.linkedEquipmentIds.length === 0)
    )
  }

  public getAssetEndOfLifeYear(equipmentId: number) {
    this.assetsService.getAssetLifecycle(equipmentId).subscribe(
      (lifecycle) => {
        this.endOfLifeYear = lifecycle.planned_eol;
    });
  }
}

